import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Seo } from "@/components/Seo";
import { FadeUp } from "@/components/anim";
import { api } from "@/lib/api";
import { fmtDate, FALLBACK_IMAGE } from "@/lib/site";

const chipClass = (active) =>
  `px-4 h-10 inline-flex items-center text-xs font-bold uppercase tracking-[0.12em] border-[0.5px] transition-colors ${
    active
      ? "bg-foreground text-background border-foreground"
      : "bg-transparent text-muted-foreground border-foreground/40 hover:border-foreground hover:text-foreground"
  }`;

export default function Blog() {
  const [posts, setPosts] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");

  useEffect(() => {
    api
      .get("/blog")
      .then((res) => setPosts(res.data))
      .catch(() => setPosts([]));
  }, []);

  const visiblePosts = useMemo(() => (posts || []).filter((p) => p.category !== "Past Students"), [posts]);

  const categories = useMemo(() => {
    const set = new Set(visiblePosts.map((p) => p.category).filter(Boolean));
    return ["all", ...set];
  }, [visiblePosts]);

  const filtered = useMemo(() => {
    return visiblePosts.filter((p) => {
      const matchesSearch =
        !search ||
        p.title.toLowerCase().includes(search.toLowerCase()) ||
        (p.excerpt || "").toLowerCase().includes(search.toLowerCase());
      const matchesCat = category === "all" || p.category === category;
      return matchesSearch && matchesCat;
    });
  }, [visiblePosts, search, category]);

  const [featured, ...rest] = filtered;

  return (
    <>
      <Seo
        title="Blog & News"
        description={"News, exam success stories and class spotlights from Kerry's — Whitstable's fun, inclusive dance and performing arts school."}
        path="/blog"
      />
      <section className="grain border-b-[0.5px] border-foreground/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 sm:pt-20 sm:pb-24">
          <FadeUp className="max-w-3xl">
            <p className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.3em] text-muted-foreground mb-6">News &middot; exam results &middot; studio life</p>
            <h1 className="font-display uppercase text-6xl sm:text-8xl lg:text-[6.5rem] leading-[0.88]">
              The noticeboard
            </h1>
            <p className="mt-8 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              What&rsquo;s happening at Kerry&rsquo;s &mdash; straight from the studio.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="flex flex-col md:flex-row gap-5 md:items-center md:justify-between mb-14">
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`blog-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={chipClass(category === c)}
              >
                {c === "all" ? "All posts" : c}
              </button>
            ))}
            <Link to="/past-students" data-testid="blog-past-students-link" className={chipClass(false)}>
              Past students &rarr;
            </Link>
          </div>
          <div className="relative md:w-72">
            <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search posts…"
              className="pl-10 rounded-none border-[0.5px] border-foreground/60 bg-card h-11"
              data-testid="blog-search-input"
            />
          </div>
        </div>

        {posts === null ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(2)].map((_, i) => (
              <Skeleton key={i} className="h-96 rounded-none" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="border-[0.5px] border-dashed border-foreground/60 py-20 text-center" data-testid="blog-empty-state">
            <p className="font-display uppercase text-4xl">Nothing found</p>
            <p className="text-muted-foreground mt-3">Try a different search or category.</p>
          </div>
        ) : (
          <div className="space-y-10">
            {featured && (
              <FadeUp>
                <Link to={`/blog/${featured.slug}`} className="group grid grid-cols-1 lg:grid-cols-12 border-[0.5px] border-foreground bg-card" data-testid="blog-featured-card">
                  <div className="lg:col-span-7 overflow-hidden lg:border-r-[0.5px] border-foreground">
                    <img
                      src={featured.image_url || FALLBACK_IMAGE}
                      alt={featured.title}
                      className="w-full h-72 lg:h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                      onError={(e) => {
                        e.currentTarget.src = FALLBACK_IMAGE;
                      }}
                    />
                  </div>
                  <div className="lg:col-span-5 p-8 sm:p-12 flex flex-col justify-center border-t-[0.5px] lg:border-t-0 border-foreground">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[hsl(var(--kerrys-sea))]">
                      {featured.category} &middot; {fmtDate(featured.created_at)}
                    </p>
                    <h2 className="font-display uppercase text-4xl sm:text-5xl leading-[0.92] mt-5 group-hover:text-[hsl(var(--kerrys-coral))] transition-colors">
                      {featured.title}
                    </h2>
                    <p className="text-muted-foreground mt-5 leading-relaxed line-clamp-3">{featured.excerpt}</p>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-foreground mt-7">Read the full story &rarr;</p>
                  </div>
                </Link>
              </FadeUp>
            )}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              {rest.map((p, i) => (
                <FadeUp
                  key={p.id}
                  delay={i * 0.05}
                  className={`${i % 3 === 0 ? "md:col-span-7" : i % 3 === 1 ? "md:col-span-5 md:mt-14" : "md:col-span-6"}`}
                >
                  <Link to={`/blog/${p.slug}`} className="group block border-[0.5px] border-foreground bg-card h-full" data-testid="blog-post-card">
                    <div className="overflow-hidden border-b-[0.5px] border-foreground">
                      <img
                        src={p.image_url || FALLBACK_IMAGE}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-52 object-cover group-hover:scale-[1.03] transition-transform duration-500"
                        onError={(e) => {
                          e.currentTarget.src = FALLBACK_IMAGE;
                        }}
                      />
                    </div>
                    <div className="p-7">
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[hsl(var(--kerrys-sea))]">
                        {p.category} &middot; {fmtDate(p.created_at)}
                      </p>
                      <h3 className="font-display uppercase text-2xl sm:text-3xl leading-[0.95] mt-3 group-hover:text-[hsl(var(--kerrys-coral))] transition-colors">
                        {p.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-3 line-clamp-3">{p.excerpt}</p>
                    </div>
                  </Link>
                </FadeUp>
              ))}
            </div>
          </div>
        )}
      </section>
    </>
  );
}
